import { CrumpledPaperIcon, FaceIcon, Pencil2Icon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Link, Popover, Text, TextArea } from '@workos-inc/design-system';
import * as React from 'react';
export const HeaderFeedback: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    const handleResize = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };
    if (open) {
      addEventListener('resize', handleResize);
      return () => removeEventListener('resize', handleResize);
    }
  }, [open]);
  return <Popover.Root open={open} onOpenChange={newOpen => setOpen(newOpen)} data-sentry-element="Popover.Root" data-sentry-component="HeaderFeedback" data-sentry-source-file="header-feedback.tsx">
      <Popover.Trigger aria-label="Send feedback" data-sentry-element="Popover.Trigger" data-sentry-source-file="header-feedback.tsx">
        <Flex asChild align="center" display="inline-flex" gap="1" data-sentry-element="Flex" data-sentry-source-file="header-feedback.tsx">
          <Link asChild color="gray" data-sentry-element="Link" data-sentry-source-file="header-feedback.tsx">
            <button ref={triggerRef}>
              <Pencil2Icon data-sentry-element="Pencil2Icon" data-sentry-source-file="header-feedback.tsx" /> Feedback?
            </button>
          </Link>
        </Flex>
      </Popover.Trigger>

      <Popover.Content align="center" height="180px" size="1" width="320px" data-sentry-element="Popover.Content" data-sentry-source-file="header-feedback.tsx">
        <FeedbackForm onCancel={() => setOpen(false)} data-sentry-element="FeedbackForm" data-sentry-source-file="header-feedback.tsx" />
      </Popover.Content>
    </Popover.Root>;
};
interface FeedbackFormProps {
  onCancel: () => void;
}
type FormState = 'idle' | 'sending' | 'sent' | 'error';
const FeedbackForm = ({
  onCancel
}: FeedbackFormProps) => {
  const [message, setMessage] = React.useState('');
  const [formState, setFormState] = React.useState<FormState>('idle');

  // Message is stored in local storage so it syncs
  // across tabs and isn't lost if there was an error.
  React.useEffect(() => {
    const storedMessage = window.localStorage.getItem('@workos/feedback');
    if (storedMessage) {
      setMessage(storedMessage);
    }
  }, []);
  React.useEffect(() => {
    window.localStorage.setItem('@workos/feedback', message);
  }, [message]);
  if (formState === 'sent') {
    return <Flex align="center" direction="column" gap="2" height="100%" justify="center" pb="2" px="9" style={{
      textAlign: 'center'
    }}>
        <FaceIcon height="24" width="24" />
        <Flex align="center" direction="column">
          <Text size="2" weight="bold">
            Thank you!
          </Text>
          <Text as="p" color="gray" size="2">
            Your feedback helps us improve WorkOS.
          </Text>
        </Flex>
      </Flex>;
  }
  if (formState === 'error') {
    return <Flex align="center" direction="column" gap="2" height="100%" justify="center" pb="2" px="8" style={{
      textAlign: 'center'
    }}>
        <CrumpledPaperIcon height="24" width="24" />
        <Flex align="center" direction="column">
          <Text size="2" weight="bold">
            Huh, something went wrong
          </Text>
          <Text as="p" color="gray" size="2">
            We saved your message in this browser. Please retry later.
          </Text>
        </Flex>
      </Flex>;
  }
  return <form style={{
    display: 'contents'
  }} onSubmit={async event => {
    event.preventDefault();
    if (message) {
      setFormState('sending');
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/feedback`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message
          })
        });
        if (response.ok) {
          setMessage('');
          setFormState('sent');
        } else {
          throw Error('Couldn’t fetch the API.');
        }
      } catch {
        setFormState('error');
      }
      return;
    }
    event.currentTarget.querySelector('textarea')?.focus();
  }} data-sentry-component="FeedbackForm" data-sentry-source-file="header-feedback.tsx">
      <Flex direction="column" gap="3" height="100%" data-sentry-element="Flex" data-sentry-source-file="header-feedback.tsx">
        <Box asChild flexGrow="1" data-sentry-element="Box" data-sentry-source-file="header-feedback.tsx">
          <TextArea name="feedback" placeholder="Help us improve the docs" readOnly={formState === 'sending'} value={message} onChange={event => setMessage(event.target.value)} data-sentry-element="TextArea" data-sentry-source-file="header-feedback.tsx" />
        </Box>

        <Flex gap="2" justify="end" data-sentry-element="Flex" data-sentry-source-file="header-feedback.tsx">
          <Button color="gray" size="1" onClick={() => onCancel()} data-sentry-element="Button" data-sentry-source-file="header-feedback.tsx">
            Cancel
          </Button>
          <Button color="purple" loading={formState === 'sending'} size="1" type="submit" data-sentry-element="Button" data-sentry-source-file="header-feedback.tsx">
            Send
          </Button>
        </Flex>
      </Flex>
    </form>;
};